import { z } from "zod/v4"
import { ManagedImageId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { assertNever } from "../lib/assert-never"

export enum ManagedImageUsageType {
  SystemNotification = "system_notification",
  ShopBanner = "shop_banner",
  ShopCustomTag = "shop_custom_tag",
  StampCard = "stamp_card",
  HomeBanner = "home_banner",
  PersonalNotification = "personal_notification",
  Push = "push",
}

export const ManagedImageUsageTypeSchema = z.enum(ManagedImageUsageType)

export function convertManagedImageUsageTypeToDisplayString(usageType: ManagedImageUsageType): string {
  switch (usageType) {
    case ManagedImageUsageType.SystemNotification:
      return "運営からのお知らせ"
    case ManagedImageUsageType.ShopBanner:
      return "ショップ"
    case ManagedImageUsageType.ShopCustomTag:
      return "カスタムタグ"
    case ManagedImageUsageType.StampCard:
      return "スタンプカード"
    case ManagedImageUsageType.HomeBanner:
      return "ホームバナー"
    case ManagedImageUsageType.PersonalNotification:
      return "あなた宛のお知らせ"
    case ManagedImageUsageType.Push:
      return "Push通知"
    default:
      return assertNever(usageType)
  }
}

export const ManagedImageDocumentMeta = z.object({
  name: z.string().optional().describe("画像名"),
  usage_types: z.array(ManagedImageUsageTypeSchema).describe("用途リスト"),
  planning_management_id: z.string().nullable().describe("企画管理用の Uniq ID（管理番号）。最大12文字。AES-CBC で暗号化された文字列が格納される。").optional(),
  planning_name: z.string().nullable().describe("企画管理用名称。最大50文字。AES-CBC で暗号化された文字列が格納される。").optional(),
})

export type ManagedImageDocumentMeta = z.infer<typeof ManagedImageDocumentMeta>

export const ManagedImageDocumentBody = z.intersection(
  ManagedImageDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type ManagedImageDocumentBody = z.infer<typeof ManagedImageDocumentBody>

export type ManagedImageDocument = ManagedImageDocumentBody & {
  id: ManagedImageId,
}


export class ManagedImageDocuments {

  static toDocumentData(managedImage: ManagedImageDocument): object {
    return {
      ...managedImage,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ManagedImageDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: ManagedImageId(doc.id),
      name: data["name"],
      usage_types: data["usage_types"],
      planning_management_id: data["planning_management_id"] ?? null,
      planning_name: data["planning_name"] ?? null,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateManagedImageRequest = z.object({
  name: z.string().optional(),
  usageTypes: z.array(ManagedImageUsageTypeSchema).min(1, "用途を1つ以上選択してください"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
})

export type CreateManagedImageRequest = z.infer<typeof CreateManagedImageRequest>

export const UpdateManagedImageRequest = z.object({
  name: z.string().optional(),
  usageTypes: z.array(ManagedImageUsageTypeSchema).min(1, "用途を1つ以上選択してください"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
})

export type UpdateManagedImageRequest = z.infer<typeof UpdateManagedImageRequest>

export const ManagedImageBulkItemRequest = z.object({
  name: z.string().optional(),
  usageTypes: z.array(ManagedImageUsageTypeSchema).min(1),
  objectId: z.string(),
})

export type ManagedImageBulkItemRequest = z.infer<typeof ManagedImageBulkItemRequest>

export const CreateManagedImagesBulkRequest = z.object({
  images: z.array(ManagedImageBulkItemRequest).min(1).max(10),
})

export type CreateManagedImagesBulkRequest = z.infer<typeof CreateManagedImagesBulkRequest>

export const ReplaceManagedImageRequest = z.object({
  objectId: z.string().min(1, "objectId is required"),
})

export type ReplaceManagedImageRequest = z.infer<typeof ReplaceManagedImageRequest>

export const UpdateManagedImageUsageTypesRequest = z.object({
  usageTypes: z.array(ManagedImageUsageTypeSchema).min(1, "用途を1つ以上選択してください"),
})

export type UpdateManagedImageUsageTypesRequest = z.infer<typeof UpdateManagedImageUsageTypesRequest>

export const UpdateManagedImagePlanningRequest = z.object({
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
})

export type UpdateManagedImagePlanningRequest = z.infer<typeof UpdateManagedImagePlanningRequest>
